import axios from "./axiosInstance";
import { parseRosterFlagsResponse, RosterFlagsFetcher } from "./rosterFlagsAPI";
import config from "./config";
import { captureKioskError, addBreadcrumb } from "../utils/sentry";
import { localTodayYmd } from "@mediatechgroup/calendar-date";
import { safeGetItem, safeRemoveItem, safeSetItem } from "@mediatechgroup/app-version";

const BASE_URL = config.BASE_URL;

// ============================================
// TYPES
// ============================================

export interface KioskUser {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  role: string;
  tenant_id: string;
}

export interface KioskDevice {
  id: string;
  name: string | null;
}

export interface KioskAuthResponse {
  success: boolean;
  message: string;
  token: string;
  user: KioskUser;
  device?: KioskDevice;
}

export interface KioskProgram {
  id: string;
  key: string;
  label: string;
  description: string | null;
  imageUrl: string | null;
  slots: KioskSlot[];
  totalEnrolled: number;
}

export interface KioskSlot {
  id: string;
  slotId: string;
  time: string;
  days: string[];
  times: string[];
  durationHours: number | null;
  enrolledCount: number;
}

export interface KioskAthlete {
  id: string;
  firstName: string;
  lastName: string;
  isPresent: boolean;
}

export interface KioskModeConfig {
  requireAthletePin: boolean;
  pinLength: number;
  isEnabled: boolean;
  /** Staff attendance mode (kiosk_config.staff_mode_enabled) — the tenant's opt-in. */
  staffModeEnabled?: boolean;
}

export interface KioskModeConfigResponse {
  success: boolean;
  data: KioskModeConfig;
}

export interface AthleteEnrollment {
  slotId: string;
  programId: string;
  programName: string;
  programLabel: string;
  days: string[];
  times: string[];
  alreadyMarked: boolean;
}

export interface AthletePinAuthResponse {
  success: boolean;
  message?: string;
  data?: {
    athleteId: string;
    athleteName: string;
    enrollments: AthleteEnrollment[];
  };
}

export interface AthleteSearchResult {
  id: string;
  firstName: string;
  lastName: string;
  hasEnrollment: boolean;
}

export interface AthleteSearchResponse {
  success: boolean;
  data: AthleteSearchResult[];
}

export interface AttendanceStatus {
  slotId: string;
  programId: string;
  programName: string;
  alreadyMarked: boolean;
}

export interface AthleteAttendanceStatusResponse {
  success: boolean;
  data: {
    athleteId: string;
    date: string;
    attendance: AttendanceStatus[];
  };
}

export interface KioskProgramsResponse {
  success: boolean;
  data: KioskProgram[];
  meta: {
    date: string;
    dayOfWeek: string;
    dayIndex: number;
  };
}

export interface KioskSlotsResponse {
  success: boolean;
  data: KioskSlot[];
  meta: {
    programId: string;
    date: string;
    dayOfWeek: string;
  };
}

export interface KioskAthletesResponse {
  success: boolean;
  data: KioskAthlete[];
  meta: {
    slotId: string;
    programId: string;
    date: string;
    totalAthletes: number;
    presentCount: number;
  };
}

export interface DeviceRegistrationResponse {
  success: boolean;
  message: string;
  deviceToken: string;
  device: KioskDevice;
}

// ============================================
// LOCAL STORAGE HELPERS
// ============================================

const KIOSK_TOKEN_KEY = "kiosk_token";
const KIOSK_DEVICE_TOKEN_KEY = "kiosk_device_token";
const KIOSK_USER_KEY = "kiosk_user";

export const saveKioskToken = (token: string) => {
  safeSetItem(KIOSK_TOKEN_KEY, token);
};

export const getKioskToken = (): string | null => {
  return safeGetItem(KIOSK_TOKEN_KEY);
};

export const saveKioskDeviceToken = (deviceToken: string) => {
  safeSetItem(KIOSK_DEVICE_TOKEN_KEY, deviceToken);
};

export const getKioskDeviceToken = (): string | null => {
  return safeGetItem(KIOSK_DEVICE_TOKEN_KEY);
};

export const saveKioskUser = (user: KioskUser) => {
  safeSetItem(KIOSK_USER_KEY, JSON.stringify(user));
};

export const getKioskUser = (): KioskUser | null => {
  const user = safeGetItem(KIOSK_USER_KEY);
  return user ? JSON.parse(user) : null;
};

export const clearKioskSession = () => {
  safeRemoveItem(KIOSK_TOKEN_KEY);
  safeRemoveItem(KIOSK_USER_KEY);
  // Note: We don't clear device token - that persists for auto-login
};

export const clearKioskDeviceToken = () => {
  safeRemoveItem(KIOSK_DEVICE_TOKEN_KEY);
};

export const isKioskAuthenticated = (): boolean => {
  return !!getKioskToken() && !!getKioskUser();
};

export const hasRegisteredDevice = (): boolean => {
  return !!getKioskDeviceToken();
};

/**
 * The server ends a kiosk session when the kiosk PIN changes or the device is
 * removed (backend kioskAuthenticate answers 401 with one of these codes).
 * The kiosk then forgets its session AND its device token, so it goes back to
 * the PIN screen instead of retrying a session that will never work again.
 */
export const KIOSK_SESSION_ENDED_EVENT = "kiosk-session-ended";
const KIOSK_SESSION_ENDED_CODES = ["KIOSK_PIN_CHANGED", "KIOSK_DEVICE_REVOKED"];

export const endKioskSessionIfServerEndedIt = (error: any): boolean => {
  const code = error?.response?.data?.code;
  if (error?.response?.status !== 401 || !KIOSK_SESSION_ENDED_CODES.includes(code)) return false;
  clearKioskSession();
  clearKioskDeviceToken();
  try {
    window.dispatchEvent(
      new CustomEvent(KIOSK_SESSION_ENDED_EVENT, { detail: { message: error.response.data.error } })
    );
  } catch {
    // No window (tests without jsdom): the cleared storage is what matters.
  }
  return true;
};

// Every kiosk request goes through the shared axios instance; watch its
// failures for a session the server has ended.
(axios as any)?.interceptors?.response?.use?.(
  (response: any) => response,
  (error: any) => {
    if (error?.config?.kioskSession) endKioskSessionIfServerEndedIt(error);
    return Promise.reject(error);
  }
);

// ============================================
// API HELPER
// ============================================

const getKioskHeaders = (token?: string) => ({
  "Content-Type": "application/json",
  authorization: token ? `Bearer ${token}` : "",
});

// ============================================
// AUTHENTICATION APIS
// ============================================

/**
 * Authenticate with PIN code
 */
export const authenticateWithPin = async (pin: string): Promise<KioskAuthResponse> => {
  try {
    const headers = getKioskHeaders();
    addBreadcrumb("Kiosk PIN auth attempt", "kiosk");

    const response = await axios.post(
      `${BASE_URL}/kiosk/auth/pin`,
      { pin },
      { headers }
    );

    if (response.data?.success && response.data?.token) {
      saveKioskToken(response.data.token);
      saveKioskUser(response.data.user);
      addBreadcrumb("Kiosk PIN auth successful", "kiosk");
    }

    return response.data;
  } catch (error: unknown) {
    const err = error as { response?: { data?: { error?: string } }; message?: string };
    captureKioskError(error instanceof Error ? error : new Error(String(error)), "authenticateWithPin");

    return {
      success: false,
      message: err.response?.data?.error || err.message || "Authentication failed",
      token: "",
      user: {} as KioskUser,
    };
  }
};

/**
 * Authenticate with device token
 */
export const authenticateWithDeviceToken = async (
  deviceToken: string
): Promise<KioskAuthResponse> => {
  const response = await axios.post(
    `${BASE_URL}/kiosk/auth/device`,
    { deviceToken },
    { headers: getKioskHeaders() }
  );

  if (response.data?.success && response.data?.token) {
    saveKioskToken(response.data.token);
    saveKioskUser(response.data.user);
  }

  return response.data;
};

/**
 * Get kiosk mode configuration (no auth required)
 * Determines if athlete PIN mode is enabled
 */
export const getKioskModeConfig = async (): Promise<KioskModeConfigResponse> => {
  try {
    const response = await axios.get(`${BASE_URL}/kiosk/config/mode`, {
      headers: getKioskHeaders(),
    });
    return response.data;
  } catch (error: unknown) {
    captureKioskError(error instanceof Error ? error : new Error(String(error)), "getKioskModeConfig");
    return {
      success: false,
      data: {
        requireAthletePin: false,
        pinLength: 4,
        isEnabled: false,
        staffModeEnabled: false,
      },
    };
  }
};

/**
 * Authenticate athlete with their PIN
 * Returns athlete's enrollments for today
 */
export const authenticateWithAthletePin = async (
  pin: string
): Promise<AthletePinAuthResponse> => {
  try {
    addBreadcrumb("Athlete PIN auth attempt", "kiosk");
    const response = await axios.post(
      `${BASE_URL}/kiosk/auth/athlete-pin`,
      { pin },
      { headers: getKioskHeaders() }
    );
    return response.data;
  } catch (error: unknown) {
    const err = error as { response?: { data?: { error?: string } } };
    captureKioskError(error instanceof Error ? error : new Error(String(error)), "authenticateWithAthletePin");
    return {
      success: false,
      message: err.response?.data?.error || "Invalid PIN",
    };
  }
};

/**
 * Register current device for persistent login
 */
export const registerDevice = async (
  deviceName?: string
): Promise<DeviceRegistrationResponse> => {
  const token = getKioskToken();
  if (!token) {
    throw new Error("Not authenticated");
  }

  const response = await axios.post(
    `${BASE_URL}/kiosk/auth/register-device`,
    { deviceName },
    { headers: getKioskHeaders(token), kioskSession: true }
  );

  if (response.data?.success && response.data?.deviceToken) {
    saveKioskDeviceToken(response.data.deviceToken);
  }

  return response.data;
};

/**
 * Try to auto-login with stored device token
 */
export const tryAutoLogin = async (): Promise<KioskAuthResponse | null> => {
  const deviceToken = getKioskDeviceToken();
  if (!deviceToken) {
    return null;
  }

  try {
    return await authenticateWithDeviceToken(deviceToken);
  } catch (error) {
    // Device token invalid or expired - clear it
    clearKioskDeviceToken();
    return null;
  }
};

// ============================================
// DATA APIS
// ============================================

/**
 * Get programs for today with enrolled athletes
 */
export const getTodaysPrograms = async (): Promise<KioskProgramsResponse> => {
  const token = getKioskToken();
  if (!token) {
    throw new Error("Not authenticated");
  }

  const response = await axios.get(`${BASE_URL}/kiosk/programs`, {
    headers: getKioskHeaders(token),
    kioskSession: true,
  });

  return response.data;
};

/**
 * Get slots for a specific program for today
 */
export const getProgramSlots = async (
  programId: string
): Promise<KioskSlotsResponse> => {
  const token = getKioskToken();
  if (!token) {
    throw new Error("Not authenticated");
  }

  const response = await axios.get(`${BASE_URL}/kiosk/programs/${programId}/slots`, {
    headers: getKioskHeaders(token),
    kioskSession: true,
  });

  return response.data;
};

/**
 * Get enrolled athletes for a specific slot
 */
export const getSlotAthletes = async (
  slotId: string
): Promise<KioskAthletesResponse> => {
  const token = getKioskToken();
  if (!token) {
    throw new Error("Not authenticated");
  }

  const response = await axios.get(`${BASE_URL}/kiosk/slots/${slotId}/athletes`, {
    headers: getKioskHeaders(token),
    kioskSession: true,
  });

  return response.data;
};

/**
 * Mark an athlete as present
 * @param athleteId - Athlete ID
 * @param slotId - Slot ID
 * @param isMakeup - If true, marks as makeup attendance (athlete not enrolled in this slot)
 */
export const markAttendance = async (
  athleteId: string,
  slotId: string,
  isMakeup: boolean = false
): Promise<{ success: boolean; message: string; error?: string }> => {
  const token = getKioskToken();
  if (!token) {
    throw new Error("Not authenticated");
  }

  const response = await axios.post(
    `${BASE_URL}/kiosk/attendance`,
    { athleteId, slotId, isMakeup },
    { headers: getKioskHeaders(token), kioskSession: true }
  );

  return response.data;
};

/**
 * Search athletes by name (for makeup attendance)
 * Requires minimum 3 characters
 */
export const searchAthletesByName = async (
  query: string
): Promise<AthleteSearchResponse> => {
  const token = getKioskToken();
  if (!token) {
    throw new Error("Not authenticated");
  }

  if (query.length < 3) {
    return { success: true, data: [] };
  }

  try {
    const response = await axios.get(
      `${BASE_URL}/kiosk/athletes/search`,
      {
        headers: getKioskHeaders(token),
        kioskSession: true,
        params: { q: query },
      }
    );
    return response.data;
  } catch (error: unknown) {
    captureKioskError(error instanceof Error ? error : new Error(String(error)), "searchAthletesByName", { query });
    return { success: false, data: [] };
  }
};

/**
 * Get athlete's attendance status for today
 * Used to check if already marked present for specific programs/slots
 */
export const getAthleteAttendanceStatus = async (
  athleteId: string
): Promise<AthleteAttendanceStatusResponse> => {
  const token = getKioskToken();
  if (!token) {
    throw new Error("Not authenticated");
  }

  try {
    const response = await axios.get(
      `${BASE_URL}/kiosk/athletes/${athleteId}/attendance-status`,
      { headers: getKioskHeaders(token), kioskSession: true }
    );
    return response.data;
  } catch (error: unknown) {
    captureKioskError(error instanceof Error ? error : new Error(String(error)), "getAthleteAttendanceStatus", { athleteId });
    return {
      success: false,
      data: {
        athleteId,
        date: localTodayYmd(),
        attendance: [],
      },
    };
  }
};

/**
 * Remove attendance (undo during 3-second window)
 */
export const removeAttendance = async (
  athleteId: string,
  slotId: string
): Promise<{ success: boolean; message: string }> => {
  const token = getKioskToken();
  if (!token) {
    throw new Error("Not authenticated");
  }

  const response = await axios.delete(`${BASE_URL}/kiosk/attendance`, {
    headers: getKioskHeaders(token),
    kioskSession: true,
    data: { athleteId, slotId },
  });

  return response.data;
};

// ============================================
// STAFF KIOSK MODE (kiosk_config.staff_mode_enabled)
// Class attendance from the kiosk session: the backend routes /kiosk/staff/*
// reuse the staff attendance handlers and allow only the tenant's today and
// yesterday. Every call carries the KIOSK token (never `authToken`).
// ============================================

export type KioskStaffDay = "today" | "yesterday";

/** One row of the class roster read (the staff handler's shape). */
export interface KioskStaffRosterEntry {
  athleteId: string;
  firstName?: string;
  lastName?: string;
  status: "present" | "absent" | "makeup" | "no_record";
  id: string | null;
  attendance_id?: string | null;
  isEnrolled?: boolean;
  athleteStatus?: string;
  isTrial?: boolean;
  onVacationHold?: boolean;
  vacationReturnDate?: string | null;
  account_flagged?: boolean;
  account_flag_reason?: string | null;
  [key: string]: unknown;
}

export interface KioskStaffRosterResponse {
  success: boolean;
  data: KioskStaffRosterEntry[];
  trialAttendees?: KioskStaffRosterEntry[];
}

/** A mark or clear: ok, or the backend's own reason. */
export interface KioskStaffSaveResult {
  ok: boolean;
  message?: string;
}

const kioskTokenOrThrow = (): string => {
  const token = getKioskToken();
  if (!token) throw new Error("Not authenticated");
  return token;
};

const backendMessage = (error: unknown): string | undefined => {
  const err = error as { response?: { data?: { error?: string; message?: string } } };
  return err.response?.data?.error || err.response?.data?.message;
};

/** That day's classes for the staff kiosk (today or yesterday, on the tenant's calendar). */
export const getStaffPrograms = async (day: KioskStaffDay): Promise<KioskProgramsResponse> => {
  const token = kioskTokenOrThrow();
  const response = await axios.get(`${BASE_URL}/kiosk/staff/programs`, {
    headers: getKioskHeaders(token),
    kioskSession: true,
    params: { day },
  });
  return response.data;
};

/** The class roster for a program / slot / day — the staff roster read. Throws on failure. */
export const getStaffRoster = async (
  programId: string,
  date: string,
  slotId: string
): Promise<KioskStaffRosterResponse> => {
  const token = kioskTokenOrThrow();
  const response = await axios.get(
    `${BASE_URL}/kiosk/staff/attendance/${programId}/${date}/${slotId}`,
    { headers: getKioskHeaders(token), kioskSession: true }
  );
  return response.data;
};

/**
 * The roster badges (medical alert, no photos, waiver missing) for the class
 * on screen, asked with the KIOSK sign-in. Same source as every other roster;
 * the kiosk is never sent medical detail. Throws on failure, so a roster
 * whose alerts could not be checked says so instead of looking clear.
 */
export const getStaffRosterFlags: RosterFlagsFetcher = async ({ athleteIds = [] }) => {
  const token = kioskTokenOrThrow();
  const response = await axios.post(
    `${BASE_URL}/kiosk/staff/roster-flags`,
    { athlete_ids: athleteIds },
    { headers: getKioskHeaders(token), kioskSession: true }
  );
  return parseRosterFlagsResponse(response.data);
};

/** Mark one athlete present or absent (the staff mark). */
export const markStaffAttendance = async (mark: {
  programId: string;
  timeSlotId: string;
  attendanceDate: string;
  athleteId: string;
  status: "present" | "absent";
  isTrial?: boolean;
}): Promise<KioskStaffSaveResult> => {
  try {
    const token = kioskTokenOrThrow();
    const response = await axios.post(
      `${BASE_URL}/kiosk/staff/attendance/mark`,
      {
        programId: mark.programId,
        timeSlotId: mark.timeSlotId,
        attendanceDate: mark.attendanceDate,
        attendance: [{ athleteId: mark.athleteId, status: mark.status, isTrial: !!mark.isTrial }],
      },
      { headers: getKioskHeaders(token), kioskSession: true }
    );
    return { ok: response.data?.status === "success" };
  } catch (error: unknown) {
    captureKioskError(error instanceof Error ? error : new Error(String(error)), "markStaffAttendance");
    return { ok: false, message: backendMessage(error) };
  }
};

/** Clear one athlete's mark (the staff clear). */
export const clearStaffAttendance = async (mark: {
  programId: string;
  timeSlotId: string;
  attendanceDate: string;
  athleteId: string;
}): Promise<KioskStaffSaveResult> => {
  try {
    const token = kioskTokenOrThrow();
    const response = await axios.delete(`${BASE_URL}/kiosk/staff/attendance/record`, {
      headers: getKioskHeaders(token),
      kioskSession: true,
      data: mark,
    });
    return { ok: response.data?.status === "success" };
  } catch (error: unknown) {
    captureKioskError(error instanceof Error ? error : new Error(String(error)), "clearStaffAttendance");
    return { ok: false, message: backendMessage(error) };
  }
};

// ============================================
// ADMIN APIS (for future use)
// ============================================

/**
 * Update kiosk PIN
 */
export const updateKioskPin = async (
  newPin: string
): Promise<{ success: boolean; message: string }> => {
  const token = getKioskToken();
  if (!token) {
    throw new Error("Not authenticated");
  }

  const response = await axios.put(
    `${BASE_URL}/kiosk/config/pin`,
    { newPin },
    { headers: getKioskHeaders(token), kioskSession: true }
  );

  return response.data;
};

/**
 * List registered devices
 */
export const listDevices = async (): Promise<{
  success: boolean;
  data: Array<{
    id: string;
    device_name: string | null;
    last_used_at: string;
    created_at: string;
    is_active: boolean;
  }>;
}> => {
  const token = getKioskToken();
  if (!token) {
    throw new Error("Not authenticated");
  }

  const response = await axios.get(`${BASE_URL}/kiosk/devices`, {
    headers: getKioskHeaders(token),
    kioskSession: true,
  });

  return response.data;
};

/**
 * Revoke a device
 */
export const revokeDevice = async (
  deviceId: string
): Promise<{ success: boolean; message: string }> => {
  const token = getKioskToken();
  if (!token) {
    throw new Error("Not authenticated");
  }

  const response = await axios.delete(`${BASE_URL}/kiosk/devices/${deviceId}`, {
    headers: getKioskHeaders(token),
    kioskSession: true,
  });

  return response.data;
};

// ============================================
// PUBLIC TENANT SETTINGS API
// ============================================

export interface TenantSettings {
  primary_color?: string;
  secondary_color?: string;
  logo_url?: string;
  timezone?: string;
  business_name?: string;
  // When false the kiosk hides the Make Up card and the pin-entry's
  // no-enrollment fallback shows a "no class today" error.
  allow_makeups?: boolean;
}

/**
 * Get public tenant settings (no auth required, just needs x-tenant-id)
 * Used by kiosk to get branding/theming settings
 */
export const getTenantSettings = async (): Promise<TenantSettings | null> => {
  try {
    const response = await axios.get(`${BASE_URL}/settings`, {
      headers: getKioskHeaders(), // Uses x-tenant-id but no auth token needed
    });

    if (response.data?.success) {
      return response.data.data;
    }
    return null;
  } catch (error: unknown) {
    captureKioskError(error instanceof Error ? error : new Error(String(error)), "getTenantSettings");
    return null;
  }
};
